import React, { Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Tag, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent, CardHeader } from '../components/Card';
import { BookingModule } from '../components/BookingModule';
import { WorkshopCard } from '../components/WorkshopCard';
import { QuoteCard } from '../components/QuoteCard';
import { stories } from '../data/stories';
export function WorkshopDetail() {
  const {
    id
  } = useParams();
  // Mock data for current workshop
  const workshop = {
    id,
    title: 'Macramé voor beginners',
    description: 'Leer de basis van macramé in deze workshop voor beginners. We maken een wandhanger die je mee naar huis kunt nemen. Alle materialen zijn inbegrepen.',
    longDescription: 'In deze workshop leer je de basisknopen van macramé, een oude techniek waarbij je met touw decoratieve patronen knoopt. We beginnen met een introductie over de geschiedenis en toepassingen van macramé, waarna we stap voor stap een mooie wandhanger maken. De workshop is geschikt voor absolute beginners en alle benodigde materialen zijn inbegrepen. Aan het eind van de workshop ga je naar huis met een prachtige zelfgemaakte wandhanger en de kennis om thuis verder te experimenteren met macramé.',
    date: '15 November 2023',
    time: '14:00 - 16:00',
    location: 'De Maekerij, Workshopruimte 2',
    price: '€45,00',
    theme: 'Draad',
    capacity: '12 deelnemers',
    instructor: 'Lisa Jansen'
  };
  // Mock data for related workshops
  const relatedWorkshops = [{
    id: '4',
    title: 'Borduren voor beginners',
    date: '25 Nov 2023',
    time: '13:00 - 15:00',
    theme: 'Draad'
  }, {
    id: '7',
    title: 'Gevorderd Macramé',
    date: '5 Dec 2023',
    time: '19:00 - 21:30',
    theme: 'Draad'
  }, {
    id: '8',
    title: 'Weven met natuurlijke materialen',
    date: '12 Dec 2023',
    time: '10:00 - 13:00',
    theme: 'Draad'
  }];
  return <div className="container mx-auto px-4 py-10" data-id="element-1376">
      <div className="mb-6" data-id="element-1377">
        <Link to="/workshops" className="text-gray-600 hover:underline inline-flex items-center group" data-id="element-1378">
          <ArrowLeft size={16} className="mr-1 group-hover:-translate-x-1 transition-transform duration-200" data-id="element-1379" />
          Terug naar workshops
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1380">
        <div className="lg:col-span-2" data-id="element-1381">
          <div className="border-2 border-dashed border-gray-300 h-80 flex items-center justify-center bg-gray-100 mb-6" data-id="element-1382">
            <span className="text-gray-400" data-id="element-1383">Workshop afbeelding</span>
          </div>

          <h1 className="text-3xl font-bold mb-5" data-id="element-1384">{workshop.title}</h1>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-1385">
            <div className="flex items-center" data-id="element-1386">
              <Calendar size={18} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1387" />
              <span className="text-sm" data-id="element-1388">{workshop.date}</span>
            </div>
            <div className="flex items-center" data-id="element-1389">
              <Clock size={18} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1390" />
              <span className="text-sm" data-id="element-1391">{workshop.time}</span>
            </div>
            <div className="flex items-center" data-id="element-1392">
              <MapPin size={18} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1393" />
              <span className="text-sm" data-id="element-1394">De Maekerij</span>
            </div>
            <div className="flex items-center" data-id="element-1395">
              <Users size={18} className="mr-2 text-gray-500 flex-shrink-0" data-id="element-1396" />
              <span className="text-sm" data-id="element-1397">{workshop.capacity}</span>
            </div>
          </div>

          <div className="mb-6" data-id="element-1398">
            <Link to={`/themes/${workshop.theme.toLowerCase()}`} className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm hover:bg-gray-200 transition-colors duration-150" data-id="element-1399">
              <Tag size={14} className="mr-1" data-id="element-1400" />
              {workshop.theme}
            </Link>
          </div>

          <div className="mb-8" data-id="element-1401">
            <h2 className="text-xl font-bold mb-3" data-id="element-1402">Over deze workshop</h2>
            <p className="text-gray-600 mb-5 leading-relaxed" data-id="element-1403">
              {workshop.longDescription}
            </p>

            <h3 className="font-bold mt-6 mb-3" data-id="element-1404">Wat krijg je?</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-1 mb-5 pl-1" data-id="element-1405">
              <li data-id="element-1406">Professionele begeleiding van een ervaren instructeur</li>
              <li data-id="element-1407">Alle benodigde materialen</li>
              <li data-id="element-1408">Een zelfgemaakte wandhanger om mee naar huis te nemen</li>
              <li data-id="element-1409">Koffie, thee en versnaperingen tijdens de workshop</li>
              <li data-id="element-1410">Een instructieblad om thuis verder te oefenen</li>
            </ul>

            <h3 className="font-bold mt-6 mb-3" data-id="element-1411">Instructeur</h3>
            <div className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-1412">
              <div className="border border-dashed border-gray-300 w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 mr-3 flex-shrink-0" data-id="element-1413">
                <span className="text-gray-400 text-xs" data-id="element-1414">Foto</span>
              </div>
              <div data-id="element-1415">
                <p className="font-medium" data-id="element-1416">{workshop.instructor}</p>
                <p className="text-sm text-gray-500" data-id="element-1417">Macramé specialist</p>
              </div>
            </div>
          </div>

          <div className="mb-8" data-id="element-1418">
            <h2 className="text-xl font-bold mb-4" data-id="element-1419">Gerelateerd maekerscafé</h2>
            <Card variant="outlined" data-id="element-1420">
              <CardContent data-id="element-1421">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-1422">
                  <div className="border border-dashed border-gray-300 w-full md:w-32 h-32 flex items-center justify-center bg-gray-100 flex-shrink-0" data-id="element-1423">
                    <span className="text-gray-400" data-id="element-1424">Café afbeelding</span>
                  </div>

                  <div className="flex-1" data-id="element-1425">
                    <CardHeader title="Maekerscafé Draad" data-id="element-1426" />
                    <p className="text-gray-600 mb-4" data-id="element-1427">
                      Sluit je aan bij ons maekerscafé van draad-enthousiasten
                      voor regelmatige bijeenkomsten, kennisdeling en
                      gezamenlijke projecten.
                    </p>
                    <Link to="/maekerscafés/draad" data-id="element-1428">
                      <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-1430" />} data-id="element-1429">
                        Bekijk maekerscafé
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Participant story */}
          <div className="mb-8" data-id="element-1431">
            <h2 className="text-xl font-bold mb-4" data-id="element-1432">Wat deelnemers zeggen</h2>
            <QuoteCard story={stories.filter(s => s.category === 'workshop')[1]} data-id="element-1433" />
          </div>
        </div>

        <div data-id="element-1434">
          <BookingModule price={workshop.price} data-id="element-1435" />

          <div className="mt-8" data-id="element-1436">
            <h3 className="text-xl font-bold mb-4" data-id="element-1437">Praktische informatie</h3>
            <Card variant="outlined" data-id="element-1438">
              <CardContent className="divide-y divide-gray-200" data-id="element-1439">
                <div className="py-3" data-id="element-1440">
                  <h4 className="font-medium mb-1" data-id="element-1441">Locatie</h4>
                  <p className="text-sm text-gray-600" data-id="element-1442">{workshop.location}</p>
                </div>
                <div className="py-3" data-id="element-1443">
                  <h4 className="font-medium mb-1" data-id="element-1444">Meenemen</h4>
                  <p className="text-sm text-gray-600" data-id="element-1445">
                    Niets, alle materialen zijn inbegrepen.
                  </p>
                </div>
                <div className="py-3" data-id="element-1446">
                  <h4 className="font-medium mb-1" data-id="element-1447">Niveau</h4>
                  <p className="text-sm text-gray-600" data-id="element-1448">
                    Geschikt voor beginners, geen voorkennis vereist.
                  </p>
                </div>
                <div className="py-3" data-id="element-1449">
                  <h4 className="font-medium mb-1" data-id="element-1450">Annuleringsvoorwaarden</h4>
                  <p className="text-sm text-gray-600" data-id="element-1451">
                    Tot 48 uur voor aanvang kosteloos annuleren.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <div className="mt-16" data-id="element-1452">
        <div className="flex items-center justify-between mb-6" data-id="element-1453">
          <h2 className="text-2xl font-bold" data-id="element-1454">
            Andere workshops binnen thema {workshop.theme}
          </h2>
          <Link to={`/workshops?theme=${encodeURIComponent(workshop.theme)}`} data-id="element-1455">
            <Button variant="secondary" size="medium" rightIcon={<ArrowRight size={16} data-id="element-1457" />} data-id="element-1456">
              Meer workshops binnen thema
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1458">
          {relatedWorkshops.map(workshop => <WorkshopCard key={workshop.id} {...workshop} data-id="element-1459" />)}
        </div>
      </div>
    </div>;
}